/**
 * Labstep
 *
 * @module prosemirror
 * @desc ProseMirror main entry point
 */

import { getNodeViews } from 'labstep-web/prosemirror/components/NodeView';
import {
  ReactNodeViewPortals,
  ReactNodeViewPortalsProvider,
  useReactNodeViewPortals,
} from 'labstep-web/prosemirror/components/ReactNodeViewPortals';
import { StepsOrderContextProvider } from 'labstep-web/prosemirror/context';
import { getInitialState } from 'labstep-web/prosemirror/state/initial-state';
import { EditorView } from 'prosemirror-view';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import styles from './styles.module.scss';
import type {
  IProseMirrorReadOnlyContainerProps,
  IProseMirrorReadOnlyProps,
} from './types';

export const ProseMirrorEditorReadOnly: React.FC<
  IProseMirrorReadOnlyProps
> = ({ initialState, entity, timestamp, portals }) => {
  const { createPortal } = useReactNodeViewPortals();

  const handleCreatePortal = useCallback(createPortal, []);

  const editorViewRef = useRef(null);

  const createEditorView = useCallback((editorViewDOM) => {
    new EditorView(editorViewDOM, {
      state: getInitialState(initialState, {
        entity,
        handleCreatePortal,
        premiumFeatures: [],
      }),
      nodeViews: getNodeViews(entity, handleCreatePortal, timestamp),
      editable: (): false => false,
    });
  }, []);

  useEffect(() => {
    const editorViewDOM = editorViewRef.current;
    if (editorViewDOM) {
      createEditorView(editorViewDOM);
    }
  }, [createEditorView]);

  return (
    <div
      className={styles.prosemirrorStyles}
      data-cy="prosemirror/ReadOnly"
    >
      <div ref={editorViewRef} />
      <ReactNodeViewPortals portals={portals} />
    </div>
  );
};

export const ProseMirrorContainer: RFC<
  IProseMirrorReadOnlyContainerProps
> = (props) => (
  <StepsOrderContextProvider
    initialState={props.initialState}
    timestamp={props.timestamp}
  >
    <ReactNodeViewPortalsProvider>
      {({ data }) => (
        <ProseMirrorEditorReadOnly {...props} portals={data} />
      )}
    </ReactNodeViewPortalsProvider>
  </StepsOrderContextProvider>
);
